/**
 * THE FILL REGISTRY — what the help assistant may fill on the portal page, and the only way it can (plan-019 WAPP-HOST;
 * ED-25; design §31.9.5; research `docs/research/help-form-fill-hook.md` §4, §6; FR-HA-18…23, AC-HA-9).
 *
 * The reviewed forms register their fields here through `useHelpFillable` (`./use-help-fillable.ts`), per field and per
 * mounted step, keyed by the registering component (`owner`, a `useId()`) and the router `href` it was drawn on. The
 * widget reads what is mounted (`fillable()`, OD-5's page context) and, when the person presses "Fill in", hands a
 * confirmed proposal to `fill()`, which writes each value through **the form's own setter** — exactly what typing does,
 * so the form's validation, its draft and its dirty state follow as for typed input — and answers per field `applied` or
 * `refused` with a reason. Never all-or-nothing: a field that mounts only once another is applied (`wait_minute` after
 * `attempt`) is refused this round and fillable on a second press.
 *
 * WHAT A FILL NEVER DOES. It never submits, never sends a request (this directory imports no gateway, `fetch`,
 * `XMLHttpRequest` or `sendBeacon`: the `help-fill-no-network` rule), never calls `focus()`, `blur()` or
 * `scrollIntoView()` (FR-HA-18), never touches the page's DOM, and never stores a value anywhere but in this tab's memory
 * for undo (FR-HA-23).
 *
 * REFUSALS, in the order they are checked (design §31.9.5): a never-fill form (`never_fill`) or anything outside the
 * registry (`not_in_registry`); suspended (`suspended`: a `409 scope_changed` until the auth_session is read again, a
 * step-up or any modal dialog over the page); the person lacks the form's capability (`capability_missing`); the form
 * is not on the page (`form_not_mounted`); the router is mid-navigation or the registration was drawn on another `href`
 * (`route_changed`); per field, not in the form's registry (`field_not_registered`), not mounted now
 * (`form_not_mounted`), a value that does not parse for its kind or that the form does not accept now (`value_invalid`).
 *
 * AMENDED 2026-10-18 (owner: "fill the pages by my instructions"; ADR 0072). Two additions:
 * - **Component fields.** A form that takes them (`componentField: true`) also holds fields the shared controls register
 *   themselves inside its `HelpFillScope` (`./help-fill-scope.tsx`), each port carrying its own `spec` — kind, label,
 *   choices — checked with the registry's `componentFieldSpecOf` (never a code naming a never-actionable category, never
 *   a code the form registers itself). Their free text is reported as filled or empty only; the page snapshot carries
 *   it when the person allowed whole-page reading.
 * - **The opening-hours change set.** An `opening_hours` proposal names only what changes; the value written through
 *   the editor's setter is that change merged into the editor's whole state (`mergedFillValue`), so the editor's
 *   validation, dirty state and draft see one ordinary edit, and an undo puts the whole state back.
 *
 * UNDO restores, through the same setters, only fields still mounted by the same component and still holding the value
 * the fill put there; anything else is skipped ("changed since" / "no longer on screen"). It never submits. A newer fill
 * of the same field supersedes the older proposal's undo for it.
 */
import {
  HELP_FORM_REGISTRY,
  checkFillProposal,
  componentFieldSpecOf,
  helpFormFieldCodes,
  helpFormFieldOf,
  isHelpFormId,
  isNeverFillFormId,
  mergedFillValue,
  sameFillValue as sameDomainFillValue,
  type ComponentFieldReport,
  type FillableKind,
  type HelpActionRefusalReason,
  type HelpFillValue,
  type HelpFormId,
  type NamedCapability,
} from "@wetalk/domain";
import type { FillResult, MountedFillableField, MountedFillableForm, UndoResult } from "@wetalk/help-widget";

/** Why fills are refused for a while (FR-HA-21; design §31.9.6). */
export const HELP_FILL_SUSPENSION = ["scope_changed", "step_up"] as const;
export type HelpFillSuspension = (typeof HELP_FILL_SUSPENSION)[number];

/** One registered field, as the registering form exposes it. Functions read the form's latest render. */
export interface HelpFieldPort {
  /** The field's current value, or `null` while it holds no answer. */
  readonly read: () => HelpFillValue | null;
  /** The form's dynamic allow-list (the offered languages, the offered options) beyond the registry's static bounds. */
  readonly accepts: (value: HelpFillValue) => boolean;
  /** The form's OWN setter — the one its `onChange` calls. */
  readonly set: (value: HelpFillValue) => void;
  /** Back to "not answered", for an undo whose `before` was no answer; `null` when the form has no such state. */
  readonly unset: (() => void) | null;
  /**
   * ADR 0072: a component-registered field's own spec — its kind, its label and the choices it offers now; absent for a
   * field the form registers in `HELP_FORM_REGISTRY` (whose spec is the registry's).
   */
  readonly spec?: () => ComponentFieldReport & { readonly label: string; readonly optionLabel?: readonly string[] };
}

/** One component's registration: the fields of one form it has mounted now. */
export interface HelpFillRegistration {
  /** The registering component (`useId()`): a late cleanup of an old instance never removes a new one. */
  readonly owner: string;
  readonly formId: HelpFormId;
  /** The router `href` the component was drawn on. */
  readonly href: string;
  /** The mounted fields, by registered code. */
  readonly field: Readonly<Record<string, HelpFieldPort>>;
}

/** What the registry asks of the page around it; the host supplies it (`../host-api.ts`). */
export interface HelpFillEnvironment {
  /** Whether the person holds the capability in the open scope now (ADR 0033's evaluator over the shell's state). */
  readonly holds: (capability: NamedCapability) => boolean;
  /** The router's location and whether it is idle (no navigation pending). */
  readonly location: () => { readonly href: string; readonly idle: boolean };
  /** Whether a modal dialog stands over the page (a step-up, a confirmation): the form behind it is not filled. */
  readonly modalOpen: () => boolean;
}

export interface HelpFillRegistry {
  /** Registers one component's mounted fields; the returned function unregisters exactly that registration. */
  register(registration: HelpFillRegistration): () => void;
  /** The registered forms on the page now, fields in the registry's fill order, with their current values. */
  fillable(): readonly MountedFillableForm[];
  /** Applies a confirmed proposal, synchronously, in the registry's fill order. */
  fill(formId: string, proposalId: string, field: Readonly<Record<string, unknown>>): FillResult;
  undo(proposalId: string): UndoResult;
  /** Suspends fills for `reason` until the returned function is called (holders are counted). */
  suspend(reason: HelpFillSuspension): () => void;
  /** The reasons fills are suspended for now (a modal over the page is checked at fill time). */
  suspension(): readonly HelpFillSuspension[];
  /** The value the last fill put in this field, while it stands; `undefined` when none does. */
  filledValue(formId: HelpFormId, code: string): HelpFillValue | undefined;
  /** A registered form's values changed (typed input): the widget re-sends page context. */
  valuesChanged(): void;
  /** For `useSyncExternalStore`: a number that changes on every change. */
  snapshot(): number;
  subscribe(listener: () => void): () => void;
}

interface UndoEntry {
  readonly formId: HelpFormId;
  readonly code: string;
  readonly owner: string;
  readonly before: HelpFillValue | null;
  readonly after: HelpFillValue;
  superseded: boolean;
}

/** Equality of two fill values (scalars, flat arrays, opening hours by meaning — the domain's `sameFillValue`). */
export function sameFillValue(left: HelpFillValue | null | undefined, right: HelpFillValue | null | undefined): boolean {
  if (left === undefined || right === undefined) return left === right;
  return sameDomainFillValue(left, right);
}

function markKeyOf(formId: string, code: string): string {
  return `${formId}\u0000${code}`;
}

export function createHelpFillRegistry(environment: HelpFillEnvironment): HelpFillRegistry {
  const registration = new Map<string, HelpFillRegistration>();
  const suspended = new Map<HelpFillSuspension, number>();
  const filled = new Map<string, HelpFillValue>();
  const undoRecord = new Map<string, UndoEntry[]>();
  const listener = new Set<() => void>();
  let changeCount = 0;

  function notify(): void {
    changeCount += 1;
    for (const one of [...listener]) one();
  }

  /** The registration holding a form's field now (the latest registered, when two components hold the same code). */
  function portOf(formId: HelpFormId, code: string): { readonly owner: string; readonly href: string; readonly port: HelpFieldPort } | null {
    let found: { readonly owner: string; readonly href: string; readonly port: HelpFieldPort } | null = null;
    for (const one of registration.values()) {
      const port = one.formId === formId ? one.field[code] : undefined;
      // A registered code is the form's own binding; a component may hold only a code the registry does not list.
      if (port !== undefined && (port.spec === undefined) === (helpFormFieldOf(formId, code) !== null)) found = { owner: one.owner, href: one.href, port };
    }
    return found;
  }

  /** A form's component-registered fields (codes the registry does not list for it), the latest registration each. */
  function componentPortsOf(formId: HelpFormId): Map<string, { readonly owner: string; readonly href: string; readonly port: HelpFieldPort }> {
    const found = new Map<string, { readonly owner: string; readonly href: string; readonly port: HelpFieldPort }>();
    for (const one of registration.values()) {
      if (one.formId !== formId) continue;
      for (const [code, port] of Object.entries(one.field)) {
        if (port.spec === undefined || helpFormFieldOf(formId, code) !== null) continue;
        const spec = port.spec();
        if (componentFieldSpecOf(formId, spec) === null) continue;
        found.set(code, { owner: one.owner, href: one.href, port });
      }
    }
    return found;
  }

  function refuseEvery(field: Readonly<Record<string, unknown>>, reason: HelpActionRefusalReason): FillResult {
    return { applied: [], refused: Object.keys(field).map((code) => ({ code, reason })) };
  }

  return {
    register(next) {
      registration.set(next.owner, next);
      notify();
      return () => {
        if (registration.get(next.owner) !== next) return;
        // A component re-registers under the same owner when its set of mounted fields changes (cleanup, then the new
        // registration, in one commit): its undo entries and marks stay. Gone for good, they lapse when next read.
        registration.delete(next.owner);
        notify();
      };
    },

    fillable() {
      const form: MountedFillableForm[] = [];
      for (const formId of Object.keys(HELP_FORM_REGISTRY) as HelpFormId[]) {
        const field: MountedFillableField[] = [];
        for (const code of helpFormFieldCodes(formId)) {
          const found = portOf(formId, code);
          const spec = helpFormFieldOf(formId, code);
          if (found === null || spec === null) continue;
          const value = found.port.read();
          const kind: FillableKind = spec.kind;
          field.push(value === null ? { code, kind } : { code, kind, value });
        }
        // ADR 0072: the fields the form's shared controls registered themselves, in the order they mounted.
        for (const [code, found] of componentPortsOf(formId)) {
          const spec = found.port.spec?.();
          if (spec === undefined) continue;
          const value = found.port.read();
          const filled = value !== null && !(typeof value === "string" && value.trim() === "");
          const option = spec.option === undefined ? undefined : spec.option.map((one, index) => ({ value: one, label: spec.optionLabel?.[index] ?? one }));
          field.push({
            code,
            kind: spec.kind as FillableKind,
            label: spec.label,
            ...(option === undefined ? {} : { option }),
            // Free text the person typed is reported as filled or empty only (the snapshot carries it when allowed).
            ...(value === null || spec.kind === "text" ? { filled } : { value }),
          });
        }
        if (field.length > 0) form.push({ formId, field });
      }
      return form;
    },

    fill(formId, proposalId, field) {
      if (isNeverFillFormId(formId)) return refuseEvery(field, "never_fill");
      if (!isHelpFormId(formId)) return refuseEvery(field, "not_in_registry");
      if (suspended.size > 0 || environment.modalOpen()) return refuseEvery(field, "suspended");
      if (!environment.holds(HELP_FORM_REGISTRY[formId].capability)) return refuseEvery(field, "capability_missing");
      if (![...registration.values()].some((one) => one.formId === formId)) return refuseEvery(field, "form_not_mounted");
      const location = environment.location();
      if (!location.idle) return refuseEvery(field, "route_changed");

      const component = [...componentPortsOf(formId)].flatMap(([, found]) => {
        const spec = found.port.spec?.();
        return spec === undefined ? [] : [spec];
      });
      const check = checkFillProposal(
        formId,
        Object.keys(field).map((code) => ({ code, value: field[code] })),
        component,
      );
      if (check.kind === "refused") return refuseEvery(field, check.reason);

      const applied: string[] = [];
      const refused: { code: string; reason: HelpActionRefusalReason }[] = check.refused.map((one) => ({ code: one.code, reason: one.reason }));
      const entries = undoRecord.get(proposalId) ?? [];
      for (const one of check.accepted) {
        const found = portOf(formId, one.code);
        if (found === null) {
          refused.push({ code: one.code, reason: "form_not_mounted" });
          continue;
        }
        if (found.href !== location.href) {
          refused.push({ code: one.code, reason: "route_changed" });
          continue;
        }
        const before = found.port.read();
        // An opening-hours change set lands as the editor's whole state with only the named parts changed; the form
        // judges (`accepts`) and receives (`set`) that whole state, exactly as if the person had edited it.
        const written = mergedFillValue(one.kind, before, one.value) as HelpFillValue;
        if (!found.port.accepts(written)) {
          refused.push({ code: one.code, reason: "value_invalid" });
          continue;
        }
        found.port.set(written);
        for (const [otherId, other] of undoRecord) {
          if (otherId === proposalId) continue;
          for (const entry of other) if (entry.formId === formId && entry.code === one.code) entry.superseded = true;
        }
        const earlier = entries.find((entry) => entry.formId === formId && entry.code === one.code && entry.owner === found.owner);
        if (earlier === undefined) entries.push({ formId, code: one.code, owner: found.owner, before, after: written, superseded: false });
        else entries.splice(entries.indexOf(earlier), 1, { ...earlier, after: written, superseded: false });
        filled.set(markKeyOf(formId, one.code), written);
        applied.push(one.code);
      }
      if (entries.length > 0) undoRecord.set(proposalId, entries);
      if (applied.length > 0) notify();
      return { applied, refused };
    },

    undo(proposalId) {
      const entries = undoRecord.get(proposalId);
      if (entries === undefined) return { restored: [], skipped: [] };
      undoRecord.delete(proposalId);
      const restored: string[] = [];
      const skipped: { code: string; reason: "changed_since" | "not_on_screen" }[] = [];
      // Last applied first: the wait goes back before the attempt count that mounted it.
      for (const entry of [...entries].reverse()) {
        const port = registration.get(entry.owner)?.field[entry.code];
        if (port === undefined) {
          skipped.push({ code: entry.code, reason: "not_on_screen" });
          continue;
        }
        if (entry.superseded || !sameFillValue(port.read(), entry.after)) {
          skipped.push({ code: entry.code, reason: "changed_since" });
          continue;
        }
        if (entry.before === null) {
          if (port.unset === null) {
            skipped.push({ code: entry.code, reason: "changed_since" });
            continue;
          }
          port.unset();
        } else {
          port.set(entry.before);
        }
        filled.delete(markKeyOf(entry.formId, entry.code));
        restored.push(entry.code);
      }
      notify();
      return { restored, skipped };
    },

    suspend(reason) {
      suspended.set(reason, (suspended.get(reason) ?? 0) + 1);
      notify();
      let released = false;
      return () => {
        if (released) return;
        released = true;
        const left = (suspended.get(reason) ?? 1) - 1;
        if (left <= 0) suspended.delete(reason);
        else suspended.set(reason, left);
        notify();
      };
    },

    suspension: () => HELP_FILL_SUSPENSION.filter((reason) => suspended.has(reason)),

    filledValue(formId, code) {
      const key = markKeyOf(formId, code);
      if (portOf(formId, code) === null) filled.delete(key);
      return filled.get(key);
    },

    valuesChanged: notify,

    snapshot: () => changeCount,

    subscribe(one) {
      listener.add(one);
      return () => {
        listener.delete(one);
      };
    },
  };
}
